import { $generateNodesFromSerializedNodes, $insertGeneratedNodes } from '@lexical/clipboard';
import { $convertFromMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  ESCAPED_MARKDOWN_PUNCTUATION_PATTERN,
  protectEscapedMarkdownPunctuation,
  sanitizeEditorLinkUrl,
} from '@main/shared/utils';
import {
  $createLineBreakNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  createEditor,
  LexicalNode,
  PASTE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import { restoreEscapedMarkdownPunctuationInDocument } from '../../markdown/markdown-escapes';
import { MD_THEME, MD_TRANSFORMERS } from '../../markdown/transformers';
import { WysiwygBaseNodes } from '../../wysiwyg/nodes';

const BLOCK_MARKDOWN_PATTERNS = [
  /^#{1,6}\s+\S/m,
  /^>\s+\S/m,
  /^\s*[-*+]\s+\S/m,
  /^\s*\d+\.\s+\S/m,
  /```[\s\S]*```/m,
  /^\|.+\|\s*\n\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/m,
  /^\s*[^|\n]+\|[^\n]+\s*\n\s*:?-{3,}:?\s*\|\s*:?-{3,}:?/m,
];

const WHOLE_INLINE_MARKDOWN_PATTERNS = [
  /^!?\[[^\]\n]+\]\([^\s)]+\)$/,
  /^`[^`]+`$/,
  /^\*\*[^*\n]+\*\*$/,
  /^__[^_\n]+__$/,
];

const INLINE_MARKDOWN_PROSE_PATTERNS = [
  /(^|\s)\*\*[^*\n]+\*\*(?=\s|[.,;:!?)]|$)/,
  /(^|\s)__[^_\n]+__(?=\s|[.,;:!?)]|$)/,
  /(^|\s)\*[^*\s][^*\n]*[^*\s]\*(?=\s|[.,;:!?)]|$)/,
  /(^|\s)_[^_\s][^_\n]*[^_\s]_(?=\s|[.,;:!?)]|$)/,
  /`[^`\n]+`/,
  /!?\[[^\]\n]+\]\([^\s)]+\)/,
  /~~[^~\n]+~~/,
];

export function MarkdownPastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent | InputEvent | KeyboardEvent) => {
        if (!(event instanceof ClipboardEvent) || !editor.isEditable()) {
          return false;
        }

        const clipboardData = event.clipboardData;
        if (!clipboardData) {
          return false;
        }

        const text = clipboardData.getData('text/plain');
        const markdownText = normalizeMarkdownPasteText(text);
        if (!isHighConfidenceMarkdown(markdownText)) {
          return false;
        }

        const html = clipboardData.getData('text/html');
        if (html && !isPlainTextHtmlClipboard(html, text)) {
          return false;
        }

        event.preventDefault();
        const markdown = sanitizeMarkdown(protectEscapedMarkdownPunctuation(markdownText));
        const tempEditor = createEditor({
          nodes: WysiwygBaseNodes,
          theme: MD_THEME,
          onError: (error) => {
            throw error;
          },
        });

        tempEditor.update(
          () => {
            $convertFromMarkdownString(markdown, MD_TRANSFORMERS);
            restoreEscapedMarkdownPunctuationInDocument();
            splitJoinedBlocks();
          },
          { discrete: true },
        );

        const serializedNodes = tempEditor.getEditorState().toJSON().root.children as Parameters<
          typeof $generateNodesFromSerializedNodes
        >[0];

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }

          const nodes = $generateNodesFromSerializedNodes(serializedNodes);
          $insertGeneratedNodes(editor, nodes, selection);
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}

function isPlainTextHtmlClipboard(html: string, text: string) {
  if (typeof DOMParser === 'undefined') {
    return false;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return !doc.querySelector(RICH_HTML_SELECTOR) || htmlTextMatchesPlainText(doc.body, text);
}

function htmlTextMatchesPlainText(body: HTMLElement, text: string) {
  return normalizeClipboardText(getHtmlClipboardText(body)) === normalizeClipboardText(text);
}

function getHtmlClipboardText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
    return '';
  }

  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : undefined;
  const tagName = element?.tagName.toLowerCase();
  if (tagName === 'br') {
    return '\n';
  }

  const childText = Array.from(node.childNodes).map(getHtmlClipboardText).join('');
  if (tagName && HTML_TEXT_BLOCK_TAGS.has(tagName)) {
    return `${childText}\n`;
  }

  return childText;
}

function normalizeClipboardText(text: string) {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function normalizeMarkdownPasteText(text: string) {
  return text.replace(/\r\n?/g, '\n').replace(/\u00a0/g, ' ');
}

const HTML_TEXT_BLOCK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'footer',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
]);

const RICH_HTML_SELECTOR = [
  'a[href]',
  'blockquote',
  'code',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img',
  'li',
  'ol',
  'pre',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
].join(',');

function splitJoinedBlocks() {
  for (const node of $getRoot().getChildren()) {
    if (!$isElementNode(node) || node.getType() === 'code') {
      continue;
    }

    const textNodes = node.getAllTextNodes();
    for (const textNode of textNodes) {
      const text = textNode.getTextContent();
      if (!text.includes('\n')) {
        continue;
      }

      const parts = text.split('\n');
      const replacementNodes = parts.flatMap((part, index) => {
        const partNode = $createTextNode(part);
        partNode.setFormat(textNode.getFormat());
        partNode.setStyle(textNode.getStyle());
        return index === parts.length - 1 ? [partNode] : [partNode, $createLineBreakNode()];
      });

      const [firstReplacementNode, ...remainingReplacementNodes] = replacementNodes;
      if (!firstReplacementNode) {
        continue;
      }

      textNode.replace(firstReplacementNode);
      let previousNode: LexicalNode = firstReplacementNode;
      for (const replacementNode of remainingReplacementNodes) {
        previousNode.insertAfter(replacementNode);
        previousNode = replacementNode;
      }
    }

    const firstChild = node.getFirstChild();
    const lastChild = node.getLastChild();
    if ($isTextNode(firstChild) && firstChild.getTextContent() === '') {
      firstChild.remove();
    }
    if ($isTextNode(lastChild) && lastChild.getTextContent() === '') {
      lastChild.remove();
    }
  }
}

function isHighConfidenceMarkdown(text: string) {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length < 3) {
    return false;
  }

  return (
    BLOCK_MARKDOWN_PATTERNS.some((pattern) => pattern.test(trimmed)) ||
    WHOLE_INLINE_MARKDOWN_PATTERNS.some((pattern) => pattern.test(trimmed)) ||
    INLINE_MARKDOWN_PROSE_PATTERNS.some((pattern) => pattern.test(trimmed)) ||
    ESCAPED_MARKDOWN_PUNCTUATION_PATTERN.test(trimmed)
  );
}

function sanitizeMarkdown(markdown: string) {
  return markdown
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, label: string, url: string) => {
      const safeLabel = label.trim() || url.trim();
      return `[${safeLabel}](${sanitizeMarkdownLinkUrl(url.trim())})`;
    })
    .replace(/\[([^\]\n]*)\]\(([^)\s]+)\)/g, (_match, label: string, url: string) => {
      return `[${label}](${sanitizeMarkdownLinkUrl(url.trim())})`;
    });
}

function sanitizeMarkdownLinkUrl(url: string) {
  return sanitizeEditorLinkUrl(url) ?? '#';
}
